/** @fileoverview Builds the read-only globals values from one UTC clock snapshot and the signed-in viewer. */

import type { FabrikGlobalField, FabrikGlobals } from '../../types/apps';
import type { FabrikViewer } from '../../types/identity';

/* The lowest and highest dates, for effective date ranges open at one end. */
export const LOW_DATE = '1900-01-01';
export const HIGH_DATE = '9999-12-31';

/** Creates the read-only globals values from one clock snapshot. */
export const createFabrikGlobals = (viewer?: FabrikViewer, now = new Date()): FabrikGlobals => {
  const currentDateTime = now.toISOString();
  const currentDate = currentDateTime.slice(0, 10);
  const midnightUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return {
    currentDate,
    currentDateSap: `/Date(${midnightUtc})/`,
    currentDateTime,
    currentDateTimeSap: `/Date(${now.getTime()})/`,
    lowDate: LOW_DATE,
    highDate: HIGH_DATE,
    currentUserFirstName: viewer?.firstName ?? '',
    currentUserLastName: viewer?.lastName ?? '',
    currentUserEmail: viewer?.email ?? '',
  };
};

/** Every globals field and the kind of value it holds. */
export const GLOBALS_FIELD_TYPES: Readonly<Record<FabrikGlobalField, 'date' | 'email' | 'text'>> =
  Object.freeze({
    currentDate: 'date',
    currentDateSap: 'date',
    currentDateTime: 'date',
    currentDateTimeSap: 'date',
    lowDate: 'date',
    highDate: 'date',
    currentUserFirstName: 'text',
    currentUserLastName: 'text',
    currentUserEmail: 'email',
  });

/** Globals fields the server sets itself, so a clock snapshot sent to it leaves them out. */
export const SERVER_SET_GLOBAL_FIELDS: ReadonlySet<FabrikGlobalField> = new Set([
  'lowDate',
  'highDate',
]);

/**
 * Tells whether a name is a globals field.
 * @param field the field name to check
 */
export const isGlobalField = (field: unknown): field is FabrikGlobalField =>
  typeof field === 'string' && Object.prototype.hasOwnProperty.call(GLOBALS_FIELD_TYPES, field);

/**
 * Tells whether a row filter or join ref has the OData field type it needs: a date or time
 * globals field needs the target field type, so its value is written in the right OData form.
 * @param ref the row filter value or the join `on` ref
 * @param fieldType the target OData field type, when given
 */
export const hasRequiredODataFieldType = (
  ref: { scope?: unknown; field?: unknown },
  fieldType: unknown
): boolean =>
  ref.scope !== 'globals' ||
  !isGlobalField(ref.field) ||
  GLOBALS_FIELD_TYPES[ref.field] !== 'date' ||
  Boolean(fieldType);
